import { ToggleButtonGroup, ToggleButton } from '@epilot/journey-elements'
import { CHART_TYPES, type ChartType } from '@epilot/journey-logic-commons'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { IsScreenUnder } from '../../../../utils/helper'

import { usePriceHistoryChartStyles } from './styles'
import { buildChartOptions } from './utils'

type PriceHistoryChartProps = {
  data: Record<ChartType, { date: number; price: number }[]>
  availableCharts: ChartType[]
  isLoading?: boolean
}

export const PriceHistoryChart = ({
  availableCharts,
  data
}: PriceHistoryChartProps) => {
  const { t, i18n } = useTranslation()
  const classes = usePriceHistoryChartStyles()

  const chartTypes: ChartType[] = CHART_TYPES.filter((chartType) =>
    availableCharts.includes(chartType)
  )

  const charts = useMemo(
    () => buildChartOptions(chartTypes, data, t),
    [chartTypes, data, t]
  )

  const [selectedChart, setSelectedChart] = useState<ChartType | undefined>(
    chartTypes.length > 0 ? chartTypes[chartTypes.length - 1] : undefined
  )
  const [options, setOptions] = useState<Highcharts.Options | undefined>(
    selectedChart ? charts[selectedChart]?.options : undefined
  )

  // TODO: move to dropdown menu on mobile after move to Concorde
  const useShortLabels = IsScreenUnder(440) && Object.entries(charts).length > 3

  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: ChartType
  ) => {
    if (newValue !== null) {
      setSelectedChart(newValue)

      const options = charts[newValue]?.options ?? {}

      setOptions(options)
    }
  }

  return (
    <div className={classes.chartContainer} lang={i18n.language}>
      <div className={classes.chartButtonGroup}>
        <ToggleButtonGroup
          aria-label="chart type"
          exclusive
          onChange={handleChange}
          size="medium"
          value={selectedChart}
        >
          {chartTypes.map((chartType) => (
            <ToggleButton key={chartType} value={chartType}>
              {useShortLabels
                ? (charts[chartType].short_label ?? charts[chartType].label)
                : charts[chartType].label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        key={selectedChart}
        options={options}
      />
    </div>
  )
}
